import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import PageSeo from "@/components/PageSeo";
import { getAvailability, createRequest, memberProfile } from "@/lib/api";
import { WizardLayout, allLegal } from "@/pages/wizardCommon";
import { buildPrimary, buildModules, buildRoleOpts, buildCityOpts, cityLabel, PRIORITY_KEYS, URGENCY_MULT } from "@/pages/book/bookConfig";
import { BookStep1, BookStep2, BookStep3, BookStep4 } from "@/pages/book/BookSteps";
import { usePageEvent } from "@/lib/analytics";

export default function BookService() {
  const { L } = useLang();
  const nav = useNavigate();
  // Quote/booking request started — fires once when the form opens.
  usePageEvent({ event: "quote_request_start", lead_type: "quote_request", business: "dukk" });
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ legal: {} });
  const [slots, setSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [mb, setMb] = useState(null);
  const [editAc, setEditAc] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const tk = localStorage.getItem("tb_member");
    if (!tk) return;
    memberProfile(tk).then((p) => {
      setMb(p);
      const acParam = new URLSearchParams(window.location.search).get("ac");
      const a = (p.aircraft || []).find((x) => x.aircraftId === acParam) || (p.aircraft || [])[0];
      setF((prev) => ({
        ...prev,
        urgency: "membership",
        memberId: p.member?.memberId,
        operationalContactName: prev.operationalContactName || p.member?.name || "",
        ...(a ? {
          aircraftId: a.aircraftId,
          aircraftType: prev.aircraftType || "jet",
          aircraftModel: prev.aircraftModel || a.model || "",
          tailNumber: prev.tailNumber || a.tailNumber || "",
          fboHangarHandler: prev.fboHangarHandler || a.fboHangar || "",
          airport: prev.airport || a.base || "",
          discretionMode: prev.discretionMode ?? !!a.discretionMode,
        } : {}),
      }));
    }).catch(() => {});
  }, []);

  const PRIMARY = buildPrimary(L);
  const MODULES = buildModules(L);
  const PRICE = {}, MOD_LABEL = {};
  Object.values(MODULES).forEach((g) => g.items.forEach(([k, lbl, pr]) => { PRICE[k] = pr; MOD_LABEL[k] = lbl; }));

  const modules = f.modules || [];
  const baseEstimate = modules.reduce((a, k) => a + (PRICE[k] || 0), 0);
  const estimate = Math.round(baseEstimate * (URGENCY_MULT[f.urgency] || 1));
  const toggleModule = (k) => {
    const nextMods = modules.includes(k) ? modules.filter((x) => x !== k) : [...modules, k];
    set("modules", nextMods);
    if (!mb && nextMods.some((m) => PRIORITY_KEYS.includes(m)) && !["priority", "immediate"].includes(f.urgency)) set("urgency", "priority");
  };
  const pickPrimary = (p) => {
    if (p.membership) { nav("/membership"); return; }
    set("primaryNeed", p.v);
    set("modules", p.pre || []);
    set("urgency", mb ? "membership" : (p.v === "urgent" ? "priority" : "scheduled"));
  };

  const isUrgent = f.primaryNeed === "urgent" || modules.some((m) => PRIORITY_KEYS.includes(m));
  const goldFlow = isUrgent || ["priority", "immediate", "membership"].includes(f.urgency);

  useEffect(() => {
    if (step === 3) getAvailability(f.city && f.city !== "other" ? cityLabel(f.city) : undefined).then(setSlots).catch(() => setSlots([]));
  }, [step, f.city]);

  const next = () => setStep((s) => s + 1);

  const s1ok = !!f.primaryNeed && modules.length > 0 && !!f.urgency;
  const s2ok = f.aircraftType && f.aircraftModel && (f.aircraftType !== "other" || f.aircraftTypeOther);
  const completionFuture = !!f.requiredCompletionTime && new Date(f.requiredCompletionTime) > new Date();
  const s3ok = f.city && (f.city !== "other" || f.cityOther) && f.airport && f.fboHangarHandler && f.preferredServiceWindow && completionFuture;
  const contactOk = f.preferredContactMethod && f.contactValue;
  const s4ok = f.operationalContactName && (f.operationalContactRole && (f.operationalContactRole !== "other" || f.operationalContactRoleOther)) && contactOk && f.canConfirmAccess && allLegal(f.legal) && (!isUrgent || f.urgentDetails);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { legal, ...data } = f;
      const memberId = f.memberId || new URLSearchParams(window.location.search).get("m") || "";
      await createRequest("book_service", { ...data, memberId: memberId || undefined, serviceType: data.primaryNeed, readinessModules: modules.map((k) => MOD_LABEL[k]), estimatedStartingMXN: estimate, legalConfirmations: legal });
      nav("/solicitud-recibida");
      return;
    } catch (e) { alert("Error: " + (e?.response?.data?.detail || e.message)); }
    setSubmitting(false);
  };

  const roleOpts = buildRoleOpts(L);
  const cityOpts = buildCityOpts(L);

  const ctx = {
    L, nav, f, set, setF, setStep, next, mb, editAc, setEditAc,
    PRIMARY, MODULES, modules, toggleModule, pickPrimary, estimate,
    isUrgent, goldFlow, slots, completionFuture, roleOpts, cityOpts, submit, submitting,
    s1ok, s2ok, s3ok, s4ok,
  };

  return (
    <>
      <PageSeo esPath="/book" enPath="/en/book" title={L("Book Private Aircraft Detailing & Cabin Care | dukk", "Reservar Detailing y Cabin Care de Aeronave Privada | dukk")} description={L("Book private aircraft detailing, cabin reset and exterior care with dukk — configure readiness modules, urgency and FBO access in Mexico.", "Reserva detailing, reset de cabina y cuidado exterior de aeronave privada con dukk — configura módulos de readiness, urgencia y acceso FBO en México.")} />
      <WizardLayout
        overline={L("Book Service", "Reservar Servicio")} gold={goldFlow}
        step={step} total={4}
        title={[L("Configure aircraft readiness", "Configura el readiness"), L("Aircraft profile", "Perfil de la aeronave"), L("Location & readiness", "Ubicación y readiness"), L("Details & contact", "Detalles y contacto")][step - 1]}
        subtitle={[L("Choose a primary need, add modules and set urgency.", "Elige una necesidad principal, agrega módulos y define la urgencia."), L("Confirm the aircraft profile.", "Confirma el perfil de la aeronave."), L("Confirm location and required readiness window.", "Confirma ubicación y ventana de readiness."), L("Final details before concierge validation.", "Últimos detalles antes de la validación.")][step - 1]}
      >
        {step === 1 && <BookStep1 {...ctx} />}
        {step === 2 && <BookStep2 {...ctx} />}
        {step === 3 && <BookStep3 {...ctx} />}
        {step === 4 && <BookStep4 {...ctx} />}
      </WizardLayout>
    </>
  );
}
